import React from "react";

import styled, { keyframes } from "styled-components";


const pulse = keyframes`
  0%, 100% { opacity: 0.55; }
  50% { opacity: 1; }
`;

const Group = styled.div`
  width: 100%;
  background: #f4f5fa;
  border-radius: 12px;
  border: 1px solid #e2e5ee;
  padding: 20px;
  margin-bottom: 16px;
`;

const HeaderCard = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-left: 3px solid #d1d5db;
  border-radius: 10px;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 16px;
`;

const Bone = styled.div<{ $w: number; $h?: number }>`
  width: ${({ $w }) => $w}px;
  max-width: 100%;
  height: ${({ $h }) => $h ?? 12}px;
  border-radius: 6px;
  background: #dde1ec;
  animation: ${pulse} 1.4s ease-in-out infinite;
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

const CardRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
`;

/* Measured off the settled page: applicant attribute cards are 185px tall,
   radius 8, with a footer link row pinned to the bottom. */
const Card = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-left: 3px solid #dde1ec;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 185px;
`;

const CardFoot = styled.div`
  margin-top: auto;
  padding-top: 10px;
  border-top: 1px solid #eef0f5;
  display: flex;
  justify-content: center;
`;

const DealCard = styled(Card)`
  min-height: 120px;
`;

const SkeletonGroup = ({ delay }: { delay?: boolean }) => (
  <Group aria-hidden style={delay ? { animationDelay: "0.2s" } : undefined}>
    <HeaderCard>
      <Stack>
        <Bone $w={220} $h={16} />
        <Bone $w={300} />
      </Stack>
      <Bone $w={260} />
    </HeaderCard>
    <CardRow>
      {/* four slots — enough to read as a full row without forcing scroll;
          matches the phase-2 grid skeleton so the count never jumps */}
      {[0, 1, 2, 3].map((i) => (
        <Card key={i}>
          <Bone $w={140} $h={14} />
          <Bone $w={180} />
          <Bone $w={120} />
          <CardFoot>
            <Bone $w={96} $h={10} />
          </CardFoot>
        </Card>
      ))}
    </CardRow>
  </Group>
);

export const ApplicantSummarySkeleton = () => (
  <div data-testid="applicant-summary-skeleton">
    <SkeletonGroup />
    <SkeletonGroup delay />
  </div>
);

export const CardGridSkeleton = ({ slots }: { slots: number }) => (
  <div className="attributes-grid" aria-hidden data-testid="card-grid-skeleton">
    {Array.from({ length: slots }, (_, i) => (
      <div key={i} className="attribute-card-container">
        <Card>
          <Bone $w={110} $h={14} />
          <Bone $w={150} />
          <Bone $w={90} />
          <CardFoot>
            <Bone $w={96} $h={10} />
          </CardFoot>
        </Card>
      </div>
    ))}
  </div>
);

const FactorRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  padding-top: 20px;
  margin-bottom: 16px;
`;

export const FactorCardsSkeleton = () => (
  <FactorRow aria-hidden data-testid="factor-cards-skeleton">
    {[0, 1, 2, 3, 4].map((i) => (
      <DealCard key={i}>
        <Bone $w={130} $h={16} />
        <Bone $w={160} />
        <Bone $w={120} />
      </DealCard>
    ))}
  </FactorRow>
);
